import { useState, useEffect, useRef, useCallback } from 'react';

const MIN_INTERVAL_SECONDS = 30;

interface UseLivePollingOptions {
  dataMode: 'static' | 'live' | undefined;
  refreshIntervalSeconds: number | null | undefined;
  refreshFn: () => Promise<void>;
  enabled: boolean;
}

interface UseLivePollingReturn {
  lastRefreshedAt: Date | null;
  isPolling: boolean;
}

export function useLivePolling({
  dataMode,
  refreshIntervalSeconds,
  refreshFn,
  enabled,
}: UseLivePollingOptions): UseLivePollingReturn {
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const inFlightRef = useRef(false);
  const refreshFnRef = useRef(refreshFn);
  refreshFnRef.current = refreshFn;

  const isActive =
    dataMode === 'live' &&
    enabled &&
    typeof refreshIntervalSeconds === 'number' &&
    refreshIntervalSeconds > 0;

  const intervalMs = isActive
    ? Math.max(refreshIntervalSeconds!, MIN_INTERVAL_SECONDS) * 1000
    : 0;

  const doRefresh = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      await refreshFnRef.current();
      setLastRefreshedAt(new Date());
    } catch {
      // Swallow — don't break the polling loop
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isActive || intervalMs === 0) {
      setIsPolling(false);
      return;
    }

    setIsPolling(true);

    let timerId: ReturnType<typeof setInterval> | null = null;

    const startPolling = (): void => {
      if (timerId) return;
      timerId = setInterval(doRefresh, intervalMs);
    };

    const stopPolling = (): void => {
      if (timerId) {
        clearInterval(timerId);
        timerId = null;
      }
    };

    const handleVisibilityChange = (): void => {
      if (document.hidden) {
        stopPolling();
      } else {
        doRefresh();
        startPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      setIsPolling(false);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isActive, intervalMs, doRefresh]);

  return { lastRefreshedAt, isPolling };
}
